import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import PaymentDetailDialog from "@/components/PaymentDetailDialog";
import ExcelButton from "@/components/ExcelButton";
import FirmScorecard from "@/components/FirmScorecard";
import { fmtTL, fmtDate, fmtNum } from "@/lib/utils";

export default function FirmDetailSheet({ firmId, onClose }) {
  const [data, setData] = useState(null);
  const [payment, setPayment] = useState(null);

  useEffect(() => {
    setData(null);
    if (firmId) api.get(`/firms/${firmId}`).then(r => setData(r.data));
  }, [firmId]);

  return (
    <Sheet open={!!firmId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-4xl overflow-y-auto" data-testid="firm-detail-sheet">
        <SheetHeader><SheetTitle className="font-display text-xl">{data?.firm.name || "Yükleniyor..."}</SheetTitle></SheetHeader>
        {data && (
          <div className="mt-4 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-xs text-muted-foreground">{data.firm.merged_names?.length > 1 ? `Birleştirilen kayıtlar: ${data.firm.merged_names.join(" • ")}` : ""}</div>
              <ExcelButton params={{ firm_id: firmId }} testid="firm-excel-button" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Toplam Hakediş" value={fmtTL(data.firm.total_amount)} />
              <Kpi label="Proje" value={data.firm.project_count} />
              <Kpi label="Hakediş Sayısı" value={data.firm.payment_count} />
              <Kpi label="Çalışma Dönemi" value={`${fmtDate(data.firm.first_date)} — ${fmtDate(data.firm.last_date)}`} />
            </div>
            <Tabs defaultValue="projects">
              <TabsList>
                <TabsTrigger value="projects" data-testid="firm-tab-projects">Projeler</TabsTrigger>
                <TabsTrigger value="items" data-testid="firm-tab-items">İş Kalemleri</TabsTrigger>
                <TabsTrigger value="payments" data-testid="firm-tab-payments">Hakedişler</TabsTrigger>
                <TabsTrigger value="scorecard" data-testid="firm-tab-scorecard">Fiyat Karnesi</TabsTrigger>
              </TabsList>
              <TabsContent value="scorecard"><FirmScorecard firmId={firmId} /></TabsContent>
              <TabsContent value="projects">
                <Table>
                  <TableHeader><TableRow><TableHead>Proje</TableHead><TableHead className="text-right">Kalem</TableHead><TableHead>Dönem</TableHead><TableHead className="text-right">Tutar</TableHead></TableRow></TableHeader>
                  <TableBody>{data.projects.map(p => (
                    <TableRow key={p.project_id}><TableCell className="font-medium">{p.project_name}</TableCell><TableCell className="text-right font-mono-nums">{p.item_count}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(p.first_date)} — {fmtDate(p.last_date)}</TableCell><TableCell className="text-right font-mono-nums font-semibold">{fmtTL(p.amount)}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </TabsContent>
              <TabsContent value="items">
                <Table>
                  <TableHeader><TableRow><TableHead>Poz</TableHead><TableHead>Tanım</TableHead><TableHead className="text-right">Birim Fiyat</TableHead><TableHead className="text-right">Miktar</TableHead><TableHead className="text-right">Tutar</TableHead></TableRow></TableHeader>
                  <TableBody>{data.top_items.map((it, i) => (
                    <TableRow key={i}><TableCell className="font-mono-nums text-xs">{it.poz_no}</TableCell><TableCell className="text-sm">{it.description}</TableCell>
                      <TableCell className="text-right font-mono-nums">{fmtTL(it.unit_price)}/{it.unit}</TableCell><TableCell className="text-right font-mono-nums">{fmtNum(it.qty)}</TableCell>
                      <TableCell className="text-right font-mono-nums font-semibold">{fmtTL(it.amount)}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </TabsContent>
              <TabsContent value="payments">
                <Table>
                  <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Tarih</TableHead><TableHead>Projeler</TableHead><TableHead className="text-right">Tutar</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>{data.payments.map(p => (
                    <TableRow key={p.id}><TableCell className="font-mono-nums font-bold">#{p.payment_no}</TableCell><TableCell>{fmtDate(p.period_end)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{(p.project_names || []).join(", ")}</TableCell>
                      <TableCell className="text-right font-mono-nums font-semibold">{fmtTL(p.gross_amount)}</TableCell>
                      <TableCell><Button size="icon" variant="ghost" onClick={() => setPayment(p)} data-testid={`firm-payment-detail-${p.id}`}><Eye className="w-4 h-4" /></Button></TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </TabsContent>
            </Tabs>
          </div>
        )}
        <PaymentDetailDialog payment={payment} onClose={() => setPayment(null)} />
      </SheetContent>
    </Sheet>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-mono-nums font-bold mt-0.5 text-sm">{value}</div>
    </div>
  );
}
